import { BuyVipDialogV1Props } from '@/components/BuyVipDialogV1'
import { uiController } from '@/controllers/uiController'
import { api } from '@/helpers/api'
import { commonUtils } from '@/helpers/commonUtils'
import { dayjsHelper } from '@/helpers/dayjsHelper'
import { envHelper } from '@/helpers/envHelper'
import { storageHelper } from '@/helpers/storageHelper'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { ee } from '../eventEmitter/eventEmitter'
import { translateService, TranslateServiceType } from '../translateService'
import { TranslateServiceConsts } from '../translateService/consts'

import type { FileMaxRule, FileType } from '@/helpers/benefitsConfig'
import { getPlanFileMaxRule } from '@/helpers/benefitsConfig'
import { indexDbHelper } from '@/helpers/indexDbHelper'
import {
  GlobalModelMap,
  MODEL_ALL,
  MODEL_FAST_TRANSLATE,
  MODEL_STRATEGY_CONFIG,
  MODEL_TRANSLATE,
} from '@/helpers/modelConsts'
import { SITE_RULES } from '@/pages/PdfPro/TranslateUtil/config'
import { TranslateHelperTypes } from '@/pages/PdfPro/TranslateUtil/types'

const PRO_V_AGREE_TYPES = ['pro+_quarter_subscription', 'pro+_month_subscription']
const PRO_AGREE_TYPES = ['quarter_subscription', 'month_subscription']
type Locale = TranslateServiceType.Locale
type LocaleWithAutoDetect = TranslateServiceType.LocaleWithAutoDetect
type PaySuccessListener = () => void

/**
 * 功能类型
 * normal: 网页对照翻译
 * select: 划词翻译
 * hover: 悬停翻译
 * focus: AI 解析
 * video: 视频翻译
 * videoSummary: 视频总结
 * enhancedInput: 搜索框翻译
 * learn: 学习模式
 * chat: AI 对话
 * text: 文本翻译
 * screenshot: 截图翻译
 * image: 图片翻译
 * pdfPro: PDF 分段翻译
 * pdfUltra: PDF 无损翻译
 * word: Word 翻译
 * package: 加量包
 * context: 智能语境
 * glossary: 术语表
 * translateSpeed: 翻译线路
 * parseSpeed: 文件解析速度
 * fileSaveTime: 文件保留时长
 * fileBatchTranslate: 文件批量翻译
 * pageSummary: 网页总结
 */
export type FeatureType =
  | 'normal'
  | 'select'
  | 'hover'
  | 'focus'
  | 'video'
  | 'videoSummary'
  | 'enhancedInput'
  | 'learn'
  | 'chat'
  | 'text'
  | 'screenshot'
  | 'image'
  | 'pdfPro'
  | 'pdfUltra'
  | 'word'
  | 'package'
  | 'context'
  | 'pageSummary'
  | 'glossary'
  | 'translateSpeed'
  | 'parseSpeed'
  | 'fileSaveTime'
  | 'fileBatchTranslate'
  | 'chatPdf'
  | 'other'

export const TRANSLATE_DEFAULT_MODEL: ServerDataTypes.Model = {
  name: '微软翻译',
  desc: '',
  version: 'free',
  level: 1,
  type: 'machine',
  key: 'microsoft',
  icon: 'https://cdn.yiban.io/web/pt-web/model_logo/microsoft.png',
  ai: false,
  translate: true,
}

export const TRANSLATE_DEEPL_MODEL: ServerDataTypes.Model = {
  desc: '',
  key: 'deepl-next-new',
  level: 2,
  name: 'DeepL（新一代）',
  type: 'machine',
  version: 'pro',
  icon: '	https://cdn.yiban.io/web/pt-web/model_logo/deepl.png',
  ai: false,
  translate: true,
}

class UserService {
  @observable userData: ServerDataTypes.UserInfo | null = null

  // pdf 相关配置
  @observable pdfSourceLang: LocaleWithAutoDetect =
    TranslateServiceConsts.DEFAULT_TRANSLATE_SOURCE_LANG
  // eslint-disable-next-line max-len
  @observable pdfTargetLang: Locale = commonUtils.standardizeLocale(
    TranslateServiceConsts.DEFAULT_TRANSLATE_TARGET_LANG
  )

  // onlineTranslation 相关配置
  @observable onlineTranslationSourceLang: LocaleWithAutoDetect =
    TranslateServiceConsts.DEFAULT_TRANSLATE_SOURCE_LANG
  // eslint-disable-next-line max-len
  @observable onlineTranslationTargetLang: Locale = commonUtils.standardizeLocale(
    TranslateServiceConsts.DEFAULT_TRANSLATE_TARGET_LANG
  )

  @observable canScroll = false
  @observable dragPdfModal = false
  @observable initialed = false

  @observable extVersion = '2.1.6'
  @observable updateDate = '2025-10-20'
  @observable downloadUrl = 'https://cdn.yiban.io/extensions/会译·对照式翻译_2_1_6.zip'
  @observable hy360DownloadUrl: string =
    'https://download.chrome.360.cn/ext/prod-dgeiaiglmhdhajbpfbmajaajdlfdinpi-f4744b86aadc5454004c56566d9ab927.crx'

  @observable selectedPdfFromHome: { hash: string; file: File } | null = null
  // 在购买pro,pro+ 的弹窗支付成功，并且关闭支付成功弹窗时，触发的监听
  paySuccessfulListener: PaySuccessListener[] = []

  @observable localeList: TranslateServiceType.LocaleList = []
  // 图片翻译配置
  @observable imgSourceLang: TranslateServiceType.LocaleWithAutoDetect =
    TranslateServiceConsts.DEFAULT_TRANSLATE_SOURCE_LANG
  @observable imgTargetLang: TranslateServiceType.Locale = commonUtils.standardizeLocale(
    TranslateServiceConsts.DEFAULT_TRANSLATE_TARGET_LANG
  )
  // 在线翻译配置
  @observable onlineSourceLang: TranslateServiceType.LocaleWithAutoDetect = 'en'
  @observable onlineTargetLang: TranslateServiceType.Locale =
    commonUtils.standardizeLocale('zh-Hans')
  // 在线翻译引擎选择
  @observable onlineTranslateEngine: 'microsoft' | 'google' | 'deepl' | 'deepl-free' = 'microsoft'

  @observable siteRules: TranslateHelperTypes.SiteRule[] = SITE_RULES

  // 用户手动关闭购买pro,pro+弹窗的监听
  closePayModalListener: (() => void) | undefined = undefined
  @observable staffQrUrl: string = ''

  @observable pdfProTargetLang: Locale = commonUtils.standardizeLocale(
    TranslateServiceConsts.DEFAULT_TRANSLATE_TARGET_LANG
  )
  @observable pdfProSourceLang: string = TranslateServiceConsts.DEFAULT_TRANSLATE_SOURCE_LANG

  @observable pdfProTranslateModel: ServerDataTypes.Model = TRANSLATE_DEEPL_MODEL
  @observable pdfProGlossaryMode: TranslateServiceType.GlossaryMode = 'off'
  @observable modelList: ServerDataTypes.Model[] = []
  @observable webTranslateCompareModelExcludeModelList: string[] = []

  @observable globalModelMap: GlobalModelMap = MODEL_STRATEGY_CONFIG
  @observable wordTranslateModel: ServerDataTypes.Model = TRANSLATE_DEEPL_MODEL
  @observable wordTargetLang: Locale = commonUtils.standardizeLocale(
    TranslateServiceConsts.DEFAULT_TRANSLATE_TARGET_LANG
  )
  @observable wordSourceLang: string = TranslateServiceConsts.DEFAULT_TRANSLATE_SOURCE_LANG
  @observable wordGlossaryMode: TranslateServiceType.GlossaryMode = 'off'

  @observable pdfUltraTranslateModel: ServerDataTypes.Model = TRANSLATE_DEEPL_MODEL
  @observable pdfUltraTargetLang: Locale = commonUtils.standardizeLocale(
    TranslateServiceConsts.DEFAULT_TRANSLATE_TARGET_LANG
  )
  @observable pdfUltraSourceLang: string = TranslateServiceConsts.DEFAULT_TRANSLATE_SOURCE_LANG
  // 3天无理由退费
  @observable show3DaysRefund = false
  @observable pdfUltraGlossaryMode: TranslateServiceType.GlossaryMode = 'off'
  @computed public get translateSpeedLabel() {
    return TranslateServiceConsts.TRANSLATE_SPEED_CONFIG_OPTIONS.find(
      (item) => item.value === this.translateSpeed
    )?.label
  }
  @computed public get translateSpeed() {
    let translateSpeed: TranslateServiceType.TranslateSpeed = 'max'
    if (this.vipVersion === 'free') {
      translateSpeed = 'base'
    } else if (this.vipVersion === 'ultra') {
      translateSpeed = 'ultra'
    }
    return translateSpeed
  }

  constructor() {
    makeObservable(this)
  }

  /**
   * 读取当前版本下的最大页数和最大文件大小
   */
  public getPlanFileMaxRule(feature: FileType): FileMaxRule {
    return getPlanFileMaxRule(this.vipVersion, feature)
  }

  /**
   * 检查pdf页数是否足够
   */
  public checkPdfPageCount(
    feature: FileType,
    use: number = 1
  ): { allowed: boolean; remaining?: number } {
    if (!this.isLogin) {
      return { allowed: false, remaining: 0 }
    }

    if (feature === 'pdf_pro') {
      const remaining = this.remainPdfProPageCount
      return {
        allowed: remaining >= use,
        remaining,
      }
    }
    if (feature === 'pdf_ultra') {
      const remaining = this.remainPdfUltraPageCount
      return {
        allowed: remaining >= use,
        remaining,
      }
    }

    return { allowed: false, remaining: 0 }
  }

  @computed get newUserDiscount() {
    if (!this.userData) {
      return false
    }
    return this.userData.newUserDiscount && this.userData.versionData.version === 'free'
  }

  @computed get newUserDiscountEndTime() {
    if (this.newUserDiscount && this.userData) {
      return this.userData.createdAt * 1000 + 48 * 60 * 60 * 1000
    }
    return 0
  }

  @computed get dragTipVisibility() {
    return this.canScroll && this.dragPdfModal
  }

  @computed get hasPaid() {
    if (!this.isLogin) {
      return false
    }
    const {
      versionData: { version, endTime, vipTimeData },
    } = this.userData!
    if (version !== 'free') {
      return true
    }
    return endTime || (vipTimeData && (vipTimeData.proEndTime || vipTimeData.proPlusEndTime))
  }

  @computed get isLogin() {
    return !!this.userData
  }

  @computed get vipVersion(): ServerDataTypes.VersionType {
    if (!this.userData) {
      return 'free'
    }
    return this.userData.versionData.version
  }

  @computed get vipVersionName() {
    if (!this.userData) {
      return '免费会员'
    }
    if (this.userData.versionData.version === 'pro') {
      return 'Pro 会员'
    }
    if (this.userData.versionData.version === 'pro+') {
      return '无限会员'
    }

    if (this.userData.versionData.version === 'max') {
      return 'Max 会员'
    }

    if (this.userData.versionData.version === 'ultra') {
      return 'Ultra 会员'
    }

    return '免费会员'
  }

  @computed get vipEndTime() {
    if (!this.userData) {
      return 0
    }

    let vipEndTime = 0
    if (this.vipVersion === 'max') {
      vipEndTime = this.userData.versionData.vipTimeData.proMaxEndTime
    } else if (this.vipVersion === 'pro') {
      vipEndTime = this.userData.versionData.vipTimeData.proEndTime
    } else if (this.vipVersion === 'pro+') {
      vipEndTime = this.userData.versionData.vipTimeData.proPlusEndTime
    } else if (this.vipVersion === 'ultra') {
      vipEndTime = this.userData.versionData.vipTimeData.ultraEndTime
    }

    return vipEndTime
  }

  /** 赠送加量包额度 */
  @computed get giftPacketInfo(): ServerDataTypes.BuyPacketInfo {
    if (!this.isLogin) {
      return {
        packetAiCount: 0,
        packetPdfProCount: 0,
        totalAiPacketCount: 0,
        totalPdfProCount: 0,
        packetPicCount: 0,
        totalPicCount: 0,
        packetPdfUltraCount: 0,
        totalPdfUltraCount: 0,
        packetTopAiCount: 0,
        totalTopAiCount: 0,
      }
    }
    const {
      versionData: { giftPacketInfo },
    } = this.userData!

    return giftPacketInfo
  }

  /** 购买加量包额度 */
  @computed get buyPacketInfo(): ServerDataTypes.BuyPacketInfo {
    if (!this.isLogin) {
      return {
        packetAiCount: 0,
        packetPdfProCount: 0,
        totalAiPacketCount: 0,
        totalPdfProCount: 0,
        packetPicCount: 0,
        totalPicCount: 0,
        packetPdfUltraCount: 0,
        totalPdfUltraCount: 0,
        packetTopAiCount: 0,
        totalTopAiCount: 0,
      }
    }
    const {
      versionData: { buyPacketInfo },
    } = this.userData!
    return buyPacketInfo
  }

  // 无损翻译：
  /** 剩余每日赠送无损翻译页数 */
  @computed get remainGiftPdfUltraPageCount() {
    return this.giftPacketInfo.packetPdfUltraCount
  }

  /** 每日赠送中总的无损翻译页数 */
  @computed get totalGiftPdfUltraPageCount() {
    return this.giftPacketInfo.totalPdfUltraCount
  }

  /** 剩余加量包无损翻译页数 */
  @computed get remainBuyPdfUltraPageCount() {
    return this.buyPacketInfo.packetPdfUltraCount
  }

  /** 加量包中总无损翻译页数 */
  @computed get totalBuyPdfUltraPageCount() {
    return this.buyPacketInfo.totalPdfUltraCount
  }

  /** 会员套餐剩余无损翻译页数 */
  @computed get remainVipPdfUltraPageCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { pdfUltraPageCount },
    } = this.userData!
    return pdfUltraPageCount
  }

  /** 会员套餐中总无损翻译页数 */
  @computed get totalVipPdfUltraPageCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { pdfUltraPageTotalCount },
    } = this.userData!
    return pdfUltraPageTotalCount
  }

  /** 剩余可用无损翻译页数 */
  @computed get remainPdfUltraPageCount() {
    return (
      this.remainGiftPdfUltraPageCount +
      this.remainBuyPdfUltraPageCount +
      this.remainVipPdfUltraPageCount
    )
  }

  /** 无损翻译总页数 */
  @computed get totalPdfUltraPageCount() {
    return (
      this.totalGiftPdfUltraPageCount +
      this.totalBuyPdfUltraPageCount +
      this.totalVipPdfUltraPageCount
    )
  }

  // 分段翻译：
  /** 剩余每日赠送分段翻译页数 */
  @computed get remainGiftPdfProPageCount() {
    return this.giftPacketInfo.packetPdfProCount
  }

  /** 每日赠送中总的分段翻译页数 */
  @computed get totalGiftPdfProPageCount() {
    return this.giftPacketInfo.totalPdfProCount
  }

  /** 剩余加量包分段翻译页数 */
  @computed get remainBuyPdfProPageCount() {
    return this.buyPacketInfo.packetPdfProCount
  }

  /** 加量包中总分段翻译页数 */
  @computed get totalBuyPdfProPageCount() {
    return this.buyPacketInfo.totalPdfProCount
  }

  /** 会员套餐剩余分段翻译页数 */
  @computed get remainVipPdfProPageCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { pdfProPageCount },
    } = this.userData!
    return pdfProPageCount
  }

  /** 会员套餐中总分段翻译页数 */
  @computed get totalVipPdfProPageCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { pdfProPageTotalCount },
    } = this.userData!
    return pdfProPageTotalCount
  }

  /** 剩余可用分段翻译页数 */
  @computed get remainPdfProPageCount() {
    return (
      this.remainGiftPdfProPageCount + this.remainBuyPdfProPageCount + this.remainVipPdfProPageCount
    )
  }

  /** 分段翻译总页数 */
  @computed get totalPdfProPageCount() {
    return (
      this.totalGiftPdfProPageCount + this.totalBuyPdfProPageCount + this.totalVipPdfProPageCount
    )
  }

  // 高精翻译：
  /** 剩余高精翻译页数 */
  @computed get remainPdfHighPageCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { pdfHighPageCount },
    } = this.userData!

    return pdfHighPageCount ?? 0
  }

  /** 高精翻译总页数 */
  @computed get totalPdfHighPageCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { pdfHighPageTotalCount },
    } = this.userData!

    return pdfHighPageTotalCount ?? 0
  }

  // 图片翻译：

  /** 剩余加量包图片翻译次数 */
  @computed get remainBuyImageTranslateCount() {
    return this.buyPacketInfo.packetPicCount
  }

  /** 加量包图片翻译总数 */
  @computed get totalBuyImageTranslateCount() {
    return this.buyPacketInfo.totalPicCount
  }

  /** 会员套餐剩余图片翻译次数 */
  @computed get remainVipImageTranslateCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { prePicTranslateCountV2 },
    } = this.userData!
    return prePicTranslateCountV2
  }

  /** 会员套餐图片翻译次数总数 */
  @computed get totalVipImageTranslateCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { picTranslateTotalCountV2 },
    } = this.userData!
    return picTranslateTotalCountV2
  }

  /** 图片剩余可用次数 */
  @computed get remainImageTranslateCount() {
    return this.remainBuyImageTranslateCount + this.remainVipImageTranslateCount
  }

  /** 图片翻译总次数 */
  @computed get totalImageTranslateCount() {
    return this.totalBuyImageTranslateCount + this.totalVipImageTranslateCount
  }

  // 高级模型:
  /** 剩余每日赠送高级模型token */
  @computed get remainGiftProTokenCount() {
    return this.giftPacketInfo.packetAiCount
  }

  /** 每日赠送高级模型token总数 */
  @computed get totalGiftProTokenCount() {
    return this.giftPacketInfo.totalAiPacketCount
  }

  /** 剩余加量包高级模型token */
  @computed get remainBuyProTokenCount() {
    return this.buyPacketInfo.packetAiCount
  }

  /** 加量包高级模型token总数 */
  @computed get totalBuyProTokenCount() {
    return this.buyPacketInfo.totalAiPacketCount
  }

  /** 会员套餐剩余高级模型token */
  @computed get remainVipProTokenCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { proAiTranslateCountV2, tokenBoosterPackageCount, version },
    } = this.userData!
    if (version === 'pro+') {
      return 9999999
    }
    return proAiTranslateCountV2 + tokenBoosterPackageCount
  }

  /** 会员套餐高级模型token总数 */
  @computed get totalVipProTokenCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { proAiTranslateTotalCountV2 },
    } = this.userData!
    return proAiTranslateTotalCountV2
  }

  /** 剩余可用高级模型 Token */
  @computed get remainProTokenCount() {
    return this.remainGiftProTokenCount + this.remainBuyProTokenCount + this.remainVipProTokenCount
  }

  /** 高级模型 Token 总数 */
  @computed get totalProTokenCount() {
    return this.totalGiftProTokenCount + this.totalBuyProTokenCount + this.totalVipProTokenCount
  }

  // 顶级模型：
  /** 剩余每日赠送顶级模型token */
  @computed get remainGiftUltraTokenCount() {
    return this.giftPacketInfo.packetTopAiCount
  }

  /** 每日赠送顶级模型token总数 */
  @computed get totalGiftUltraTokenCount() {
    return this.giftPacketInfo.totalTopAiCount
  }

  /** 剩余加量包顶级模型token */
  @computed get remainBuyUltraTokenCount() {
    return this.buyPacketInfo.packetTopAiCount
  }

  /** 加量包顶级模型token总数 */
  @computed get totalBuyUltraTokenCount() {
    return this.buyPacketInfo.totalTopAiCount
  }

  /** 会员套餐剩余顶级模型token */
  @computed get remainVipUltraTokenCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { topAiCount },
    } = this.userData!

    return topAiCount
  }

  /** 会员套餐顶级模型token总数 */
  @computed get totalVipUltraTokenCount() {
    if (!this.isLogin) {
      return 0
    }
    const {
      versionData: { topAiTotalCount },
    } = this.userData!

    return topAiTotalCount
  }

  /** 剩余可用顶级模型token */
  @computed get remainUltraTokenCount() {
    return (
      this.remainGiftUltraTokenCount + this.remainBuyUltraTokenCount + this.remainVipUltraTokenCount
    )
  }

  /** 顶级模型token总数 */
  @computed get totalUltraTokenCount() {
    return (
      this.totalGiftUltraTokenCount + this.totalBuyUltraTokenCount + this.totalVipUltraTokenCount
    )
  }

  public checkTokenCountByModel(model: ServerDataTypes.Model) {
    if (!this.isLogin) {
      return 0
    }

    // 免费模型
    if (model.level === 1) {
      return 1
    }

    // 高级模型
    if (model.level === 2) {
      return this.remainProTokenCount
    }

    // 顶级模型
    if (model.level === 3) {
      return this.remainUltraTokenCount
    }

    return 0
  }

  // 当前版本到期时间
  @computed get currentVersionEndTime() {
    if (!this.userData) {
      return ''
    }
    if (this.vipVersion === 'free') {
      return ''
    }
    return dayjsHelper.formatTime(this.userData.versionData.endTime * 1000)
  }

  // 获取版本显示名称
  public getVersionDisplayName(version: ServerDataTypes.VersionType, hiddenProductName = true) {
    let planName = ''
    if (version === 'pro') {
      planName = 'Pro会员'
    } else if (version === 'pro+') {
      planName = '无限会员'
    } else if (version === 'max') {
      planName = 'Max会员'
    } else if (version === 'ultra') {
      planName = 'Ultra会员'
    } else {
      planName = '免费版'
    }
    return hiddenProductName ? planName : `${envHelper.productName} ${planName}`
  }

  @computed get alipayAgree() {
    if (!this.isLogin) {
      return null
    }
    const {
      versionData: { version, alipayAgree, agreeType },
    } = this.userData!
    if (version === 'free' || !alipayAgree) {
      return false
    }
    if (
      (version === 'pro+' && PRO_V_AGREE_TYPES.includes(agreeType)) ||
      (version === 'pro' && PRO_AGREE_TYPES.includes(agreeType)) ||
      (version === 'max' && PRO_AGREE_TYPES.includes(agreeType))
    ) {
      return true
    }
    return false
  }

  @computed get sourceLanguageOptions() {
    return this.localeList.map((item) => {
      return {
        label: item.localName,
        value: item.locale,
      }
    })
  }

  @computed get targetLanguageOptions() {
    return [...this.localeList].map(({ locale, localName }) => {
      return {
        label: localName,
        value: locale,
      }
    })
  }

  @computed get isNewUser() {
    if (!this.isLogin) {
      return false
    }
    const { createdAt } = this.userData!
    const createdAtDate = createdAt * 1000

    // 2025-06-05 14:00:00
    const targetTime = new Date('2025-06-05 14:00:00').getTime()

    // 注册时间在2025-06-05 14:00:00之后的认为是新用户
    return createdAtDate >= targetTime
  }

  public async init() {
    this.updateSEMKey()
    this.initStorageData()
    this.initLocaleList()
    this.initWebTranslateCompareModelExcludeModelList()
    this.initWordTranslateModel()
    this.initGlobalModelMap()
    this.initTranslateLanguageInfo()
    this.updateUserInfo()
    this.updateVersionInfo()
    this.initModelList()
  }
  async initModelList() {
    const dataList = await this.getLLMModelList('translate')
    this.modelList = dataList

    // 检查用户是否之前手动选择过模型
    const { pdfProUserSelectedModel } = storageHelper.get(['pdfProUserSelectedModel'])

    // 如果用户没有手动选择过模型，则根据用户类型设置默认模型
    if (!pdfProUserSelectedModel) {
      if (this.vipVersion === 'free') {
        // 免费用户也默认选择 deepl 模型
        const deeplModel = dataList.find((item) => item.key === 'deepl-classic')
        if (deeplModel) {
          this.pdfProTranslateModel = deeplModel
        }
      } else {
        // 付费用户默认选择 deepl 模型（对应题目中的 deepl-classic）
        const deeplModel = dataList.find((item) => item.key === 'deepl-classic')
        if (deeplModel) {
          this.pdfProTranslateModel = deeplModel
        }
      }
    }
    // 如果用户已经手动选择过模型，在 initStorageData 中已经恢复了，这里不需要处理
  }

  /** 获取LLM模型列表 */
  public async getLLMModelList(llmScene: PostDataTypes.LLMScene): Promise<ServerDataTypes.Model[]> {
    const { llmModelList } = storageHelper.get(['llmModelList'])
    if (llmModelList && llmModelList[llmScene] && llmModelList[llmScene].length > 0) {
      this.fetchAndSaveLLMModelList(llmScene)
      return llmModelList[llmScene]
    }
    return this.fetchAndSaveLLMModelList(llmScene)
  }

  public setPdfLang(type: 'source' | 'target', lang: Locale) {
    if (type === 'source') {
      this.pdfSourceLang = lang
      storageHelper.set({ pdfSourceLang: lang })
    } else {
      this.pdfTargetLang = lang
      storageHelper.set({ pdfTargetLang: lang })
    }
  }

  public setImgLang(type: 'source' | 'target', lang: Locale) {
    if (type === 'source') {
      this.imgSourceLang = lang
      storageHelper.set({ imgSourceLang: lang })
    } else {
      this.imgTargetLang = lang
      storageHelper.set({ imgTargetLang: lang })
    }
  }

  public setOnlineLang(type: 'source' | 'target', lang: Locale) {
    if (type === 'source') {
      this.onlineSourceLang = lang
      storageHelper.set({ onlineSourceLang: lang })
    } else {
      this.onlineTargetLang = lang
      storageHelper.set({ onlineTargetLang: lang })
    }
  }

  public setOnlineTranslateEngine(engine: 'microsoft' | 'google' | 'deepl' | 'deepl-free') {
    this.onlineTranslateEngine = engine
    // Map 'deepl-free' to 'deepl' when saving to storage
    const storageEngine = engine === 'deepl-free' ? 'deepl' : engine
    storageHelper.set({ onlineTranslateEngine: storageEngine })
  }

  /** 主动(手动)登出的情况 */
  public async logout() {
    await api.user.logout()
    this.userData = null
    commonUtils.sendExtensionMessage('logout', {})
  }

  public async updateUserInfo() {
    try {
      const { userData } = await api.user.getUserInfo()
      console.log('get user info: ', userData)
      this.userData = userData
      runInAction(() => {
        this.userData = userData
      })
      // storageHelper.set({ userData })
    } catch (error) {
      runInAction(() => {
        this.userData = null
      })
      // storageHelper.set({ userData: null })
    }
    runInAction(() => {
      this.initialed = true
    })
    ee.emit('userServiceInitialed', {})
  }

  public openLoginTab() {
    window.open(`/login?redirectUrl=${encodeURIComponent(window.location.href)}`)
  }

  public openPricingTab(type: 'buy' | 'upgrade' | 'renew' | 'wordPack' | 'jump', from: string) {
    if (type === 'jump') {
      window.open(`/pricing?from=${from}`, '_blank')
      return
    }
    const url = `/pricing?type=${type}&from=${from}&v=${
      this.vipVersion === 'free' ? 'pro' : 'pro+'
    }`
    window.open(url, '_blank')
  }

  public openOptionPage() {
    if (commonUtils.isInstalledExtension()) {
      commonUtils.sendExtensionMessage('openOptionPage', {})
    } else {
      window.open('https://huiyiai.net/install')
    }
  }

  public trackUserRecord(actionType: string) {
    const token = this.userData?.inviteCode || commonUtils.genVisitorTokenStr()
    try {
      api.user.postVisitorRecord({ token, actionType })
    } catch (error) {
      //do nothing
    }
  }

  public async updateVersionInfo() {
    try {
      const { chromeVersion, hy360DownloadUrl, extensionUpdateTime, staffQrUrl } =
        await api.getHuiyiUrlConfig()
      const hyChromeDownloadUrl = `https://cdn.yiban.io/extensions/会译·对照式翻译_${chromeVersion.replace(
        /\./g,
        '_'
      )}.zip`
      this.staffQrUrl = staffQrUrl
      this.extVersion = chromeVersion
      this.downloadUrl = hyChromeDownloadUrl
      this.hy360DownloadUrl = hy360DownloadUrl
      const updateDate = dayjsHelper.formatTime(extensionUpdateTime * 1000, 'YYYY-MM-DD')
      this.updateDate = updateDate
      console.log('version info', {
        chromeVersion,
        hyDownloadUrl: hyChromeDownloadUrl,
        hy360DownloadUrl,
        updateDate,
      })
      ee.emit('versionInfoChanged', {
        downloadUrl: hyChromeDownloadUrl,
        updateDate,
        hy360DownloadUrl,
        version: chromeVersion,
      })
    } catch (error) {
      // do nothing
    }
  }

  public updateSEMKey() {
    try {
      const { utm, kword, utm_term, bd_vid, bd_url, qhclickid, referer } = commonUtils.getUrlArgs()
      if (utm) {
        storageHelper.set({ utm })
      }
      if (
        document.referrer &&
        document.referrer.indexOf('ad.huiyi') === -1 &&
        document.referrer.indexOf('localhost') === -1
      ) {
        if (!storageHelper.get(['referer']).referer) {
          storageHelper.set({ referer: document.referrer })
        }
      }
      if (referer) {
        if (!storageHelper.get(['referer']).referer) {
          storageHelper.set({ referer: referer })
        }
      }
      if (kword) {
        const decodedKword = decodeFromGb2312(kword)
        storageHelper.set({ kword: decodedKword })
      }

      const storageReferer = storageHelper.get(['referer']).referer || ''
      if (storageReferer) {
        const refererChannel = encodeURIComponent(new URL(storageReferer).hostname)
        // www.so.com
        const { q } = commonUtils.getUrlArgs(storageReferer)
        if (refererChannel === 'www.so.com' && q) {
          storageHelper.set({ kword: q })
        }
        // cn.bing.com
        if (refererChannel === 'cn.bing.com' && utm_term) {
          storageHelper.set({ kword: utm_term })
        }
      }

      // 360 ocpc 投放参数
      if (qhclickid) {
        storageHelper.set({ qhclickid: qhclickid })
      }

      // 并不意味着 location.href 中有 bd_vid 就是从百度投放来的
      // 还有可能是从自定义投放页面来的 此时会携带 bd_url
      if (bd_vid || bd_url) {
        // 优先 bd_url 其次 location.href
        if (bd_url) {
          const bd_url_vid = commonUtils.getQueryFromUrl('bd_vid', bd_url)
          if (bd_url_vid) {
            storageHelper.set({ bdUrl: bd_url, bdVid: bd_url_vid })
            return
          }
        }
        storageHelper.set({ bdUrl: window.location.href, bdVid: bd_vid })
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  public doPay(data: {
    wordPack?: boolean
    version?: 'max' | 'ultra'
    wordPackType?: ServerDataTypes.PackagePlan['packetScene']
    paySuccess?: BuyVipDialogV1Props['paySuccess']
    from: string
    selectedPlanId?: string
    feature: FeatureType
  }) {
    const { wordPack = false, version = 'max', from, wordPackType, feature } = data
    if (wordPack) {
      uiController.buyTokenPackageDialogProps = {
        source: `${from}_v1`,
        open: true,
        type: wordPackType!,
      }
      userService.trackUserRecord('buyVipDialogV1_token')
    } else {
      // uiController.buyVipDialogV1Props = {
      //   open: true,
      //   version,
      //   source: `${source}_v1`,
      //   selectedPlanId,
      //   paySuccess,
      // }
      // 统一跳转到定价页面
      const url = `/pricing?from=${from}&v=${version}&feature=${feature}`
      window.open(url, '_blank')
    }
  }

  public addPaySuccessfulListener(listener: PaySuccessListener) {
    this.paySuccessfulListener.push(listener)
  }

  public removePaySuccessfulListener(listener: PaySuccessListener) {
    const index = this.paySuccessfulListener.findIndex((item) => item === listener)
    if (index !== -1) {
      this.paySuccessfulListener.splice(index, 1)
    }
  }

  public invokePaySuccessfulListener() {
    this.paySuccessfulListener.forEach((listener) => {
      listener()
    })
  }

  public async openPdfPage(fileUrl = '', tab: 'pdf' | 'favorite' = 'pdf') {
    const url = 'webapp/pdf'

    const urlWithParams = commonUtils.genUrl(`${envHelper.webHost}/${url}`, {
      fileUrl,
      tab,
    })

    window.open(urlWithParams, '_blank')
  }

  private initWebTranslateCompareModelExcludeModelList() {
    this.webTranslateCompareModelExcludeModelList =
      storageHelper.get(['webTranslateCompareModelExcludeModelList'])
        .webTranslateCompareModelExcludeModelList ?? []
  }

  private initLocaleList() {
    translateService.getLocaleList().then((data) => {
      this.localeList = data
    })
  }

  private initStorageData() {
    const {
      dragPdfModal,
      pdfSourceLang,
      pdfTargetLang,
      imgSourceLang,
      imgTargetLang,
      onlineSourceLang,
      onlineTargetLang,
      onlineTranslateEngine,
      pdfProUserSelectedModel,
      pdfProGlossaryMode,
      pdfUltraGlossaryMode,
      wordGlossaryMode,
    } = storageHelper.get([
      'pdfSourceLang',
      'pdfTargetLang',
      'dragPdfModal',
      'imgSourceLang',
      'imgTargetLang',
      'onlineSourceLang',
      'onlineTargetLang',
      'onlineTranslateEngine',
      'pdfProUserSelectedModel',
      'pdfProGlossaryMode',
      'pdfUltraGlossaryMode',
      'wordGlossaryMode',
    ])
    const getSourceLang = (lang: TranslateServiceType.LocaleWithAutoDetect) => {
      if (lang === TranslateServiceConsts.OLD_AUTO_DETECT_LANG) {
        return TranslateServiceConsts.AUTO_DETECT_LANG
      }
      return lang
    }
    pdfSourceLang && (this.pdfSourceLang = getSourceLang(pdfSourceLang))
    pdfTargetLang && (this.pdfTargetLang = pdfTargetLang)
    imgSourceLang && (this.imgSourceLang = getSourceLang(imgSourceLang))
    imgTargetLang && (this.imgTargetLang = imgTargetLang)
    onlineSourceLang && (this.onlineSourceLang = getSourceLang(onlineSourceLang))
    onlineTargetLang && (this.onlineTargetLang = onlineTargetLang)
    onlineTranslateEngine && (this.onlineTranslateEngine = onlineTranslateEngine)
    this.dragPdfModal = dragPdfModal ?? true

    // 如果用户之前手动选择过模型，恢复该模型
    if (pdfProUserSelectedModel) {
      this.pdfProTranslateModel = pdfProUserSelectedModel
    }
    this.pdfProGlossaryMode = pdfProGlossaryMode || 'off'
    this.pdfUltraGlossaryMode = pdfUltraGlossaryMode || 'off'
    this.wordGlossaryMode = wordGlossaryMode || 'off'
  }

  // 记录学习模式埋点
  public recordLearningMode(
    learningActionType:
      | 'click_header_learning'
      | 'click_desc_learning'
      | 'click_banner_learning'
      | 'click_learning_page_upgrade'
  ) {
    this.trackUserRecord(learningActionType)
  }

  public async postHuiyiRecord(data: PostDataTypes.UserActionInfo) {
    const dataX = {
      ...data,
      clientType: 'web',
    }
    await api.user.postHuiyiRecord(dataX)
  }

  // 保存PDF Pro文件的翻译设置
  public savePdfProFileSettings(
    fileId: number,
    settings: {
      model: ServerDataTypes.Model
      sourceLang: string
      targetLang: string
      styleType: 'dualGrey' | 'dualWhite' | 'onlyTranslation'
      imageTranslateMode: 'manual' | 'auto'
      glossaryMode: TranslateServiceType.GlossaryMode
      ContextType?: PostDataTypes.ContextType
    }
  ) {
    const currentSettings = storageHelper.get(['pdfProFileSettings'])
    const pdfProFileSettings = currentSettings.pdfProFileSettings || {}

    const currentSettingsBefore = pdfProFileSettings[fileId.toString()] || {}

    pdfProFileSettings[fileId.toString()] = {
      ...currentSettingsBefore,
      ...settings,
      savedAt: Date.now(),
    }

    storageHelper.set({ pdfProFileSettings })
  }

  // 获取PDF Pro文件的翻译设置
  public getPdfProFileSettings(fileId: number): {
    model: ServerDataTypes.Model
    sourceLang: string
    targetLang: string
    styleType: 'dualGrey' | 'dualWhite' | 'onlyTranslation'
    imageTranslateMode: 'manual' | 'auto'
    glossaryMode: TranslateServiceType.GlossaryMode
    contextType?: PostDataTypes.ContextType
  } | null {
    const currentSettings = storageHelper.get(['pdfProFileSettings'])
    const pdfProFileSettings = currentSettings.pdfProFileSettings || {}
    const fileSettings = pdfProFileSettings[fileId.toString()]

    if (!fileSettings) {
      return null
    }

    // 检查设置是否过期（超过30天）
    const now = Date.now()
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000
    if (now - fileSettings.savedAt > thirtyDaysInMs) {
      // 清理过期设置
      delete pdfProFileSettings[fileId.toString()]
      storageHelper.set({ pdfProFileSettings })
      return null
    }

    return {
      model: fileSettings.model,
      sourceLang: fileSettings.sourceLang,
      targetLang: fileSettings.targetLang,
      styleType: fileSettings.styleType,
      imageTranslateMode: fileSettings.imageTranslateMode,
      glossaryMode: fileSettings.glossaryMode,
      contextType: fileSettings.contextType,
    }
  }

  public getPdfProContext(contextKey: string): string | undefined {
    // 参数验证
    if (!contextKey || typeof contextKey !== 'string') {
      return undefined
    }

    const allPdfProContextStorage = storageHelper.get(['pdfProContext']).pdfProContext || {}
    const pdfProContextData = allPdfProContextStorage[contextKey]

    // 验证数据结构完整性
    if (
      pdfProContextData &&
      typeof pdfProContextData === 'object' &&
      typeof pdfProContextData.context === 'string' &&
      typeof pdfProContextData.savedAt === 'number'
    ) {
      return pdfProContextData.context
    }

    return undefined
  }

  public savePdfProContext(contextKey: string, context: string) {
    // 参数验证
    if (!contextKey || typeof contextKey !== 'string') {
      console.warn('Invalid contextKey provided to savePdfProContext')
      return
    }
    if (typeof context !== 'string') {
      console.warn('Invalid context provided to savePdfProContext')
      return
    }

    const allPdfProContextStorage = storageHelper.get(['pdfProContext']).pdfProContext || {}

    // 统一获取时间戳
    const now = Date.now()
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000

    // 清理30天前的数据，增加防御性检查
    Object.keys(allPdfProContextStorage).forEach((key) => {
      const item = allPdfProContextStorage[key]
      if (
        item &&
        typeof item === 'object' &&
        typeof item.savedAt === 'number' &&
        now - item.savedAt > thirtyDaysInMs
      ) {
        delete allPdfProContextStorage[key]
      }
    })

    // 使用统一的时间戳保存数据
    const saveData = { context, savedAt: now }
    allPdfProContextStorage[contextKey] = saveData
    storageHelper.set({ pdfProContext: allPdfProContextStorage })
  }

  // 保存用户手动选择的PDF Pro翻译模型
  public savePdfProUserSelectedModel(model: ServerDataTypes.Model) {
    this.pdfProTranslateModel = model
    storageHelper.set({ pdfProUserSelectedModel: model })
  }

  public savePdfProGlossaryMode(glossaryMode: TranslateServiceType.GlossaryMode) {
    this.pdfProGlossaryMode = glossaryMode || 'off'
    storageHelper.set({ pdfProGlossaryMode: glossaryMode })
  }

  // 删除PDF Pro文件的翻译设置
  public removePdfProFileSettings(fileId: number) {
    const { pdfProFileSettings } = storageHelper.get(['pdfProFileSettings'])

    if (pdfProFileSettings && pdfProFileSettings[fileId.toString()]) {
      delete pdfProFileSettings[fileId.toString()]
      storageHelper.set({ pdfProFileSettings })
    }
  }

  // 清理过期的文件设置（可以在初始化时调用）
  public cleanupExpiredPdfProFileSettings() {
    const { pdfProFileSettings } = storageHelper.get(['pdfProFileSettings'])

    if (!pdfProFileSettings) {
      return
    }

    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000
    const now = Date.now()
    let hasExpired = false

    Object.keys(pdfProFileSettings).forEach((fileId) => {
      if (now - pdfProFileSettings[fileId].savedAt > sevenDaysInMs) {
        delete pdfProFileSettings[fileId]
        hasExpired = true
      }
    })

    if (hasExpired) {
      storageHelper.set({ pdfProFileSettings })
    }
  }

  public setGlobalModelMap(globalModelMap: GlobalModelMap) {
    storageHelper.set({ globalModelMap })
  }

  private initGlobalModelMap() {
    const { globalModelMap } = storageHelper.get(['globalModelMap'])
    this.globalModelMap = globalModelMap || MODEL_STRATEGY_CONFIG
  }
  // 保存用户手动选择的PDF Pro翻译模型
  public saveWordUserSelectedModel(model: ServerDataTypes.Model) {
    this.wordTranslateModel = model
    storageHelper.set({ wordTranslateModel: model })
  }

  public savePdfUltraUserSelectedModel(model: ServerDataTypes.Model) {
    this.pdfUltraTranslateModel = model
    storageHelper.set({ pdfUltraModel: model })
  }

  public savePdfUltraGlossaryMode(glossaryMode: TranslateServiceType.GlossaryMode) {
    this.pdfUltraGlossaryMode = glossaryMode || 'off'
    storageHelper.set({ pdfUltraGlossaryMode: glossaryMode })
  }

  public saveWordGlossaryMode(glossaryMode: TranslateServiceType.GlossaryMode) {
    this.wordGlossaryMode = glossaryMode || 'off'
    storageHelper.set({ wordGlossaryMode: glossaryMode })
  }

  public initWordTranslateModel() {
    const value = storageHelper.get(['wordTranslateModel'])
    const model = value.wordTranslateModel

    if (model) {
      this.wordTranslateModel = model
      return
    }

    // 不存在，默认用DeepL
    this.wordTranslateModel = TRANSLATE_DEEPL_MODEL
  }

  public async updateGlossaryData() {
    try {
      const result = await api.user.getGlossaryList({ keyword: '' })
      const dataList = result.dataList

      const allGlossaryData = dataList.flatMap((item) =>
        item.valueList.map((subItem) => ({
          fromLang: subItem.fromLang,
          toLang: subItem.toLang,
          fromText: subItem.fromText,
          toText: subItem.toText,
          termId: subItem.termId,
          createdAt: subItem.createdAt,
        }))
      )
      console.log('@@@@ updateGlossaryData allGlossaryData', allGlossaryData)
      try {
        await indexDbHelper.batchSetGlossaryData(allGlossaryData)
      } catch (error) {
        if (error.name === 'QuotaExceededError') {
          console.error('存储空间不足，尝试清理缓存')
          await indexDbHelper.cleanOldCache('text')
          await indexDbHelper.cleanOldCache('img')
          // 重试一次
          try {
            await indexDbHelper.batchSetGlossaryData(allGlossaryData)
          } catch (retryError) {
            console.error('glossary清理后仍无法存储，跳过缓存:', retryError)
          }
        } else {
          console.error('glossary缓存存储失败:', error)
        }
      }
    } catch (error) {
      console.error('updateGlossaryData error', error)
    }
  }

  public async getGlossaryData(data: {
    fromLang: string
    toLang: string
    glossaryMode: TranslateServiceType.GlossaryMode
  }) {
    const { fromLang, toLang, glossaryMode } = data
    if (glossaryMode === 'off') {
      return []
    }
    let finalFromLang = fromLang
    const glossaryData = await indexDbHelper.batchGetGlossaryData({
      fromLang: finalFromLang,
      toLang,
    })
    return glossaryData
  }

  public saveWordLanguageInfo(sourceLang: string, targetLang: string) {
    storageHelper.set({ wordSourceLang: sourceLang, wordTargetLang: targetLang })
  }

  public savePdfProLanguageInfo(sourceLang: string, targetLang: string) {
    storageHelper.set({ pdfProSourceLang: sourceLang, pdfProTargetLang: targetLang })
  }

  public savePdfUltraLanguageInfo(sourceLang: string, targetLang: string) {
    storageHelper.set({ pdfUltraSourceLang: sourceLang, pdfUltraTargetLang: targetLang })
  }

  public calcFileExpireDayByUploadTime(fileUploadTime: number, fileType: FileType) {
    const fileSaveDay = userService.getPlanFileMaxRule(fileType).fileSaveDay - 1
    // 计算文件过期天数
    const expireDay =
      fileSaveDay - Math.floor((Date.now() - fileUploadTime * 1000) / (24 * 60 * 60 * 1000))
    return Math.floor(expireDay)
  }

  private initTranslateLanguageInfo() {
    const {
      wordSourceLang,
      wordTargetLang,
      pdfProSourceLang,
      pdfProTargetLang,
      pdfUltraSourceLang,
      pdfUltraTargetLang,
      pdfUltraModel,
      imgSourceLang,
      imgTargetLang,
    } = storageHelper.get([
      'wordSourceLang',
      'wordTargetLang',
      'pdfProSourceLang',
      'pdfProTargetLang',
      'pdfUltraSourceLang',
      'pdfUltraTargetLang',
      'pdfUltraModel',
      'imgSourceLang',
      'imgTargetLang',
    ])
    if (wordSourceLang) {
      this.wordSourceLang = wordSourceLang
    }
    if (wordTargetLang) {
      this.wordTargetLang = wordTargetLang
    }
    if (pdfProSourceLang) {
      this.pdfProSourceLang = pdfProSourceLang
    }
    if (pdfProTargetLang) {
      this.pdfProTargetLang = pdfProTargetLang
    }
    if (pdfUltraSourceLang) {
      this.pdfUltraSourceLang = pdfUltraSourceLang
    }
    if (pdfUltraTargetLang) {
      this.pdfUltraTargetLang = pdfUltraTargetLang
    }
    if (pdfUltraModel) {
      this.pdfUltraTranslateModel = pdfUltraModel
    }
    if (imgSourceLang) {
      this.imgSourceLang = imgSourceLang
    }
    if (imgTargetLang) {
      this.imgTargetLang = imgTargetLang
    }
  }

  private async fetchAndSaveLLMModelList(
    llmScene: PostDataTypes.LLMScene
  ): Promise<ServerDataTypes.Model[]> {
    try {
      const { dataList } = await api.user.getTranslateModelList(llmScene)
      let { llmModelList } = storageHelper.get(['llmModelList'])
      if (!llmModelList) {
        llmModelList = {}
      }
      llmModelList[llmScene] = dataList
      storageHelper.set({ llmModelList })
      return dataList
    } catch (error) {
      if (llmScene === 'translate') {
        return MODEL_TRANSLATE
      } else if (llmScene === 'fast_translate') {
        return MODEL_FAST_TRANSLATE
      } else {
        return MODEL_ALL
      }
    }
  }
}

export const userService = new UserService()
